import { PopperPlacementType } from "@mui/base/Popper";
import { ClickAwayListener } from "@mui/material";
import { genericMemo } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { EnterKey } from "@redotech/web-util/key";
import {
  KeyboardEvent,
  UIEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { CheckboxValue } from "../checkbox/redo-checkbox";
import { BaseRedoInput } from "../input/base-redo-text-input";
import { RedoDropdownInputSize } from "../select-dropdown/redo-single-select-dropdown-input";
import { RedoList, RedoListItem, RedoListProps } from "./redo-list";
import {
  dropdownSizeToListSize,
  listItemSizeToInputSize,
} from "./redo-list-dropdown";
import { TextRedoListItem } from "./standard-redo-list-items";

export const RedoMultiselectDropdown = genericMemo(
  function RedoMultiselectDropdown<T>({
    dropdownOpen,
    setDropdownOpen,
    dropdownAnchor,
    fitToAnchor = false,
    dropdownOffsetX,
    dropdownOffsetY,
    dropdownPlacement,
    searchString,
    setSearchString,
    searchSubmitted,
    searchPlaceholder,
    size = "xs",
    selectionVariant,
    selectAllState,
    selectAllToggled,
    selectAllLabel = "Select all",
    selectedItems,
    setSelectedItems,
    closeOnSelection,
    onBlur,
    onScroll,
    items,
    staticItems,
    staticItemSelected,
    sortBySelected,
    requireNItems: requireNItems,
    maxNItems,
    totalOptionsIncludingNotShown,
    ...redoListProps
  }: {
    closeOnSelection?: boolean;
    selectedItems: RedoListItem<T>[];
    setSelectedItems: (value: RedoListItem<T>[]) => void;
    searchString?: string;
    setSearchString?: (search: string) => void;
    searchSubmitted?: (searchString: string) => void;
    selectAllToggled?: (value: boolean) => void;
    selectAllState?: CheckboxValue;
    selectAllLabel?: string;
    searchPlaceholder?: string;
    onBlur?: () => void;
    onScroll?: UIEventHandler<HTMLDivElement>;
    dropdownOpen: boolean;
    setDropdownOpen: (open: boolean) => void;
    dropdownAnchor: HTMLElement | null;
    fitToAnchor?: boolean;
    dropdownOffsetX?: number;
    dropdownOffsetY?: number;
    dropdownPlacement?: PopperPlacementType;
    size?: RedoDropdownInputSize;
    staticItems?: RedoListItem<void>[];
    staticItemSelected?: (item: RedoListItem<void>) => void;
    sortBySelected?: boolean;
    requireNItems?: number;
    maxNItems?: number;
    /** True match count; renders a "more results" footer when it exceeds `items`. */
    totalOptionsIncludingNotShown?: number;
  } & Omit<
    RedoListProps<T>,
    | "focusedIndex"
    | "setFocusedIndex"
    | "refToListenTo"
    | "itemSelected"
    | "selectedItems"
    | "size"
    | "onItemMouseEnter"
    | "onItemMouseLeave"
  >) {
    const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
      undefined,
    );

    selectAllState = selectAllState ?? false;

    const [searchRef, setSearchRef] = useState<HTMLDivElement | null>(null);

    const isShiftHeldRef = useRef(false);
    const lastClickedIndexRef = useRef<number | undefined>(undefined);

    useEffect(() => {
      if (!dropdownOpen) {
        lastClickedIndexRef.current = undefined;
        isShiftHeldRef.current = false;
        return;
      }
      const handleKeyDown = (e: globalThis.KeyboardEvent) => {
        if (e.key === "Shift") {
          isShiftHeldRef.current = true;
        }
      };
      const handleKeyUp = (e: globalThis.KeyboardEvent) => {
        if (e.key === "Shift") {
          isShiftHeldRef.current = false;
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("keyup", handleKeyUp);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("keyup", handleKeyUp);
        isShiftHeldRef.current = false;
      };
    }, [dropdownOpen]);

    const selectedItemIds = useMemo(
      () => selectedItems.map((i) => i.id),
      [selectedItems],
    );

    const maxReached =
      maxNItems !== undefined && selectedItems.length >= maxNItems;

    const disabledItems = useMemo(() => {
      const existingDisabledItems = redoListProps.disabledItems;
      const existingDisabledItemsArray = !existingDisabledItems
        ? []
        : Array.isArray(existingDisabledItems)
          ? existingDisabledItems
          : [existingDisabledItems];
      if (!maxReached) {
        return existingDisabledItemsArray;
      }
      const selectedItemIdsSet = new Set(selectedItemIds);
      const unselectedItems = items
        .filter((i) => !selectedItemIdsSet.has(i.id))
        .map((i) => i.id);

      return [...existingDisabledItemsArray, ...unselectedItems];
    }, [maxReached, selectedItemIds, items, redoListProps.disabledItems]);

    const handleItemClicked = useHandler(
      ({
        item,
        selected,
        index,
      }: {
        item: RedoListItem<T | void>;
        selected: boolean;
        index: number;
      }) => {
        if (staticItems) {
          const staticItem = staticItems.find((i) => i.id === item.id);
          if (staticItem && staticItemSelected) {
            staticItemSelected(staticItem);
            return;
          }
        }

        const isStaticItem = staticItems?.some((i) => i.id === item.id);

        if (
          !isStaticItem &&
          isShiftHeldRef.current &&
          lastClickedIndexRef.current !== undefined
        ) {
          const start = Math.min(lastClickedIndexRef.current, index);
          const end = Math.max(lastClickedIndexRef.current, index);
          const rangeItems = items
            .slice(start, end + 1)
            .filter(
              (i) => !staticItems?.some((s) => s.id === i.id),
            ) as RedoListItem<T>[];
          const currentSelectedIds = new Set(selectedItems.map((i) => i.id));
          for (const rangeItem of rangeItems) {
            currentSelectedIds.add(rangeItem.id);
          }
          setSelectedItems(
            items.filter((i) =>
              currentSelectedIds.has(i.id),
            ) as RedoListItem<T>[],
          );
          lastClickedIndexRef.current = index;
          if (closeOnSelection) {
            setDropdownOpen(false);
          }
          return;
        }

        lastClickedIndexRef.current = isStaticItem
          ? lastClickedIndexRef.current
          : index;

        if (selected) {
          if (maxReached) {
            return;
          }
          setSelectedItems([...selectedItems, item as RedoListItem<T>]);
        } else {
          if (requireNItems && selectedItems.length === requireNItems) {
            return;
          }
          setSelectedItems(
            selectedItems.filter((selectedItem) => selectedItem.id !== item.id),
          );
        }
        if (closeOnSelection) {
          setDropdownOpen(false);
        }
      },
    );

    const [clickAwayEvent, setClickAwayEvent] = useState<undefined | false>(
      undefined,
    );

    const maybeHandleSearchSubmit = useHandler((e: KeyboardEvent) => {
      if (
        searchSubmitted &&
        searchString &&
        e.key === EnterKey &&
        focusedIndex === undefined
      ) {
        e.preventDefault();
        e.stopPropagation();
        searchSubmitted(searchString);
      }
    });

    // We need to delay the click event changing by a render cycle
    // to prevent the click that opened it from immediately closing it
    useEffect(() => {
      if (dropdownOpen) {
        setClickAwayEvent(undefined);
      } else {
        setClickAwayEvent(false);
      }
    }, [dropdownOpen]);

    const itemsWithStatic: RedoListItem<T | void>[] = useMemo(() => {
      if (!staticItems) {
        return items;
      }
      return [...items, ...staticItems];
    }, [items, staticItems]);

    return (
      <ClickAwayListener
        mouseEvent={clickAwayEvent}
        onClickAway={() => {
          setDropdownOpen(false);
          onBlur?.();
        }}
        touchEvent={clickAwayEvent}
      >
        <Dropdown
          anchor={dropdownAnchor}
          constrainHeight
          fitToAnchor={fitToAnchor}
          flexProps={{ p: "none" }}
          offsetX={dropdownOffsetX}
          offsetY={dropdownOffsetY}
          onScroll={onScroll}
          open={dropdownOpen}
          placement={dropdownPlacement}
        >
          <Flex dir="column" gap="none">
            {selectAllToggled && (
              <Flex
                borderBottomWidth="1px"
                borderColor="secondary"
                borderStyle="solid"
                px="lg"
                py="sm"
              >
                <TextRedoListItem
                  indeterminate={selectAllState === "indeterminate"}
                  onItemSelected={selectAllToggled}
                  selected={selectAllState === true}
                  selectionVariant={selectionVariant}
                  size={dropdownSizeToListSize[size]}
                  text={selectAllLabel}
                />
              </Flex>
            )}
            {setSearchString && (
              <Flex
                borderBottomWidth="1px"
                borderColor="primary"
                borderStyle="solid"
                px="lg"
                py="md"
              >
                <BaseRedoInput
                  autoFocus
                  onKeyDown={maybeHandleSearchSubmit}
                  placeholder={searchPlaceholder || "Search items..."}
                  ref={setSearchRef}
                  setValue={setSearchString}
                  size={listItemSizeToInputSize[size]}
                  value={searchString || ""}
                />
              </Flex>
            )}
            <Flex px="lg" py="md">
              <RedoList
                focusedIndex={focusedIndex}
                items={itemsWithStatic}
                itemSelected={handleItemClicked}
                refToListenTo={searchRef || dropdownAnchor}
                selectedItems={selectedItemIds}
                selectionVariant={selectionVariant}
                setFocusedIndex={setFocusedIndex}
                size={dropdownSizeToListSize[size]}
                sortBySelected={sortBySelected}
                {...redoListProps}
                disabledItems={disabledItems}
              />
            </Flex>
            {totalOptionsIncludingNotShown !== undefined &&
              totalOptionsIncludingNotShown > items.length && (
                <Flex
                  borderColor="secondary"
                  borderStyle="solid"
                  borderTopWidth="1px"
                  px="lg"
                  py="sm"
                >
                  <Text fontSize="xs" textColor="tertiary">
                    {`Showing ${items.length} of ${totalOptionsIncludingNotShown}`}
                  </Text>
                </Flex>
              )}
          </Flex>
        </Dropdown>
      </ClickAwayListener>
    );
  },
);
